<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { Icon } from '@oat-sa-private/ui-elements';
    import { __, getActualKey, generateElementId } from '@oat-sa-private/ui-core';
    import Draggable from './Draggable.svelte';

    const dispatch = createEventDispatcher();

    export let key; //key (unique identifier) of component, passed with all fired events, and also to Draggable
    export let draggableGroupKey; //for Draggable; key (unique identifier) shared between Draggables/DropAreas that are allowed to be dragged/dropped to each other
    export let removed; //item has been dropped elsewhere and doesn't belong in this list anymore, but we still need it to maintain height
    export let selected; // item has been selected for further action
    export let disabled; //disable component
    export let ariaDescribedBy; //id of html element that contains explanatory text for this item (e.g. keyboard behavior instructions)
    export let ariaGrabbed = false; //aria-grabbed for button
    export let amount = 1; // stack that much amount from this list item
    export let removable = false; // display remove icon
    export let tabbable; //tabindex=0 if true, otherwise -1
    export let placed = false; // apply class of placed item
    export let targetable = false; // on :hover, apply 'targeted' style (both for mouse :hover or drag-over)
    export let notHoverable = false; // on :hover, do not apply normal hover style (both for mouse :hover or drag-over)
    export let targeted = false; //something is being dragged-over this item (use to activate 'targetable' style, in addition to :hover)
    export let instructionsLang; // instructions lang for labels

    const fixFocusMixin = '';

    const ariaLabelElementId = generateElementId('choice-label');

    $: interactive = !removed && !disabled;

    /**
     * Handle click on item
     * @param {MouseEvent} event
     * @fires 'click'
     */
    function handleClick(event) {
        dispatch('click', {
            draggableKey: key,
            target: event.target,
            position: {
                clientX: event.clientX,
                clientY: event.clientY,
                screenX: event.screenX,
                screenY: event.screenY
            }
        });
    }

    /**
     * Prevent keydown default action for certain keys
     * Also prevent page scroll when capturing space key event
     * @param {KeyboardEvent} event
     * @returns {?Boolean} - to prevent browser default
     */
    function handleKeyDown(event) {
        const pressedKey = getActualKey(event);
        switch (pressedKey) {
            case 'enter':
            case 'space': {
                event.preventDefault();
                return false;
            }
        }
    }

    /**
     * Handle space/enter key on item
     * Parent component may have different behavior in case of mouse click and in case of space/enter key
     * @param {KeyboardEvent} event
     * @fires 'keySelect'
     */
    function handleKeyUp(event) {
        const pressedKey = getActualKey(event);
        switch (pressedKey) {
            case 'enter':
            case 'space': {
                dispatch('keySelect', {
                    draggableKey: key,
                    target: event.target,
                    key: event.key
                });
                event.stopPropagation();
                event.preventDefault();
                break;
            }
        }
    }

    /**
     * Handle click on remove button
     * @param {KeyboardEvent|PointerEvent} event
     * @fires 'remove'
     */
    function handleRemoveClick(event) {
        const data = {
            draggableKey: key,
            target: event.target
        };

        if ('key' in event) {
            data.key = event.key;
        } else if ('clientX' in event) {
            data.position = {
                clientX: event.clientX,
                clientY: event.clientY,
                screenX: event.screenX,
                screenY: event.screenY
            };
        }
        dispatch('remove', data);
    }

    /**
     * Handle space/enter key on remove button
     * @param {KeyboardEvent} event
     */
    function handleRemoveKeyUp(event) {
        const pressedKey = getActualKey(event);
        switch (pressedKey) {
            case 'enter':
            case 'space': {
                handleRemoveClick(event);
                event.stopPropagation();
                event.preventDefault();
                break;
            }
        }
    }
</script>

<style>
    .item-btn-container {
        --remover-width: 6rem;
        width: 100%;
        height: 100%;
        position: relative;

        &.last :global(.draggable-source--is-dragging) {
            visibility: hidden;
        }

        & :global(.draggable-mirror) {
            z-index: var(--layer-3);
        }
    }

    /* Button styles */

    .item-btn {
        user-select: none;
        -webkit-touch-callout: none; /* on iOS, disable contextmenu if longpress on image - it conflicts with drag-start longpress */
        width: 100%;
        height: 100%;
        min-height: 6rem;
        @add-mixin flex-center-center;
        border-radius: var(--radius-large);
        position: relative;
        @add-mixin outline-focus -1rem;
        &:focus-visible::after {
            border-radius: var(--radius-large);
        }

        & .icon-container {
            padding-inline-start: 1.75rem;
            flex: 0 0 0;
        }

        & .label-container {
            padding-block: 1.25rem;
            padding-inline: 1.25rem 1.75rem;
            flex: 1 1 auto;
        }
    }

    .item-btn {
        color: var(--color-text-default);
        background-color: var(--color-bg-default);
        border: var(--border-medium) solid var(--color-text-default);
    }

    .placed .item-btn {
        color: var(--color-text-selected);
        background-color: var(--color-bg-selected);
        border-color: var(--color-border-selected);
    }

    .disabled .item-btn {
        color: var(--color-text-disabled);
        background-color: var(--color-bg-disabled);
        border-color: var(--color-border-disabled);
    }

    .item-btn:hover,
    .item-btn:focus-visible {
        color: var(--color-text-selected);
        background-color: var(--color-bg-actionable-hover);
        border-color: var(--color-border-actionable-hover);
    }

    .selected .item-btn,
    :global(.draggable-mirror) .item-btn {
        color: var(--color-text-selected);
        background-color: var(--color-bg-actionable-hover);
        border-color: var(--color-border-actionable-hover);
        @add-mixin outline-focus -1.5rem;
        &:before {
            content: '';
            border: 1rem solid var(--color-bg-actionable-hover);
            border-radius: inherit;
            position: absolute;
            top: -0.75rem;
            bottom: -0.75rem;
            left: -0.75rem;
            right: -0.75rem;
        }
    }

    /* targeted style */
    .targetable:not(.not-hoverable) .item-btn:hover,
    .targetable:not(.not-hoverable) .item-btn:focus,
    .targetable.targeted:not(.not-hoverable) .item-btn {
        color: var(--color-brand);
        background-color: var(--color-brand-light);
        border-color: var(--color-brand-light);
        /* wider border shouldn't increase height of li, since we don't want this element to jump */
        &:before {
            content: '';
            position: absolute;
            height: calc(100% + 2 * var(--border-medium));
            width: calc(100% + 2 * var(--border-medium));
            top: calc(-1 * var(--border-medium));
            left: calc(-1 * var(--border-medium));
            box-sizing: border-box;
            border-radius: var(--radius-large);
            border: var(--border-medium-plus) dashed var(--color-brand-hover);
        }
    }

    /* unset to original (for normal) */
    :global(.draggable--is-dragging) .item-btn-container:not(.targetable) .item-btn:hover,
    .not-hoverable:not(.selected) .item-btn:hover,
    .not-hoverable:not(.selected) .item-btn:focus,
    .not-hoverable.targeted:not(.selected) .item-btn,
    :global(.draggable-source--is-dragging) .selected .item-btn {
        color: var(--color-text-default);
        background-color: var(--color-bg-default);
        border-color: var(--color-text-default);
        &:before {
            content: none;
        }
    }

    /* unset to original (for placed) */
    :global(.draggable--is-dragging) .placed:not(.targetable) .item-btn:hover,
    .placed.not-hoverable:not(.selected) .item-btn:hover,
    .placed.not-hoverable:not(.selected) .item-btn:focus,
    .placed.not-hoverable.targeted:not(.selected) .item-btn,
    :global(.draggable-source--is-dragging) .placed.selected .item-btn {
        color: var(--color-text-selected);
        background-color: var(--color-bg-selected);
        border-color: var(--color-border-selected);
        &:before {
            content: none;
        }
    }

    /* unset to original (for disabled) */
    .disabled .item-btn:hover {
        color: var(--color-text-disabled);
        background-color: var(--color-bg-disabled);
        border-color: var(--color-border-disabled);
    }

    /* if dragging starts while focused, hide outline on mirror */
    :global(.draggable-mirror) .item-btn:focus-visible:after {
        content: none;
    }

    /* Stacking styles */

    .stacking {
        position: absolute;
        width: calc(100% - 1rem);
        height: 100%;
        left: 0.5rem;
        bottom: -0.5rem;
        border: var(--border-medium) solid var(--color-border-default);
        border-radius: 1rem;
        & .amount-dragging {
            display: none;
        }
        & .amount,
        & .amount-dragging {
            position: absolute;
            bottom: -1rem;
            inset-inline-end: -1.5rem;
            font-size: 1.5rem;
            line-height: 1.5rem;
            color: var(--color-text-default);
        }
    }

    /* focus-visible shouldn't overlap stacking */
    .item-btn-container:not(.last) .item-btn {
        &:focus-visible::after {
            bottom: -1.5rem;
        }
    }

    .disabled .stacking {
        border-color: var(--color-border-disabled);
        & .amount,
        & .amount-dragging {
            color: var(--color-text-disabled);
        }
    }

    :global(.draggable-source--is-dragging) .stacking .amount {
        display: none;
    }
    :global(.draggable-source--is-dragging) .stacking .amount-dragging {
        display: inline;
    }

    /* hide stacking while dragging or selected or targeted */
    .last-dragging :global(.draggable-source--is-dragging .stacking),
    :global(.draggable-mirror) .stacking,
    .selected .stacking,
    .targetable:not(.not-hoverable):hover .stacking,
    .targetable.targeted:not(.not-hoverable) .stacking {
        opacity: 0; /* .stacking is included in the hitbox of droparea; we shouldn't change hitbox size while dragging is in progress */
    }

    /* Remover styles */

    .removable .item-btn {
        padding-block: 0;
        padding-inline: 0 var(--remover-width);
    }

    .remover {
        /* focus mixin gives position: relative, we will override below */
        @add-mixin outline-focus -1rem;
        &:focus-visible::after {
            border-radius: var(--radius-large);
        }
        cursor: pointer;
        position: absolute;
        top: 0;
        bottom: 0;
        inset-inline-end: 0;
        width: var(--remover-width);
        flex: 0 0 auto;
        align-self: stretch;
        border: var(--border-medium) solid var(--color-border-default);
        border-start-end-radius: var(--radius-large);
        border-end-end-radius: var(--radius-large);
        background: var(--color-bg-actionable-secondary);
        color: inherit;

        &:hover,
        &:focus {
            background: var(--color-bg-actionable-secondary-hover);
        }
        &:disabled,
        &.visually-disabled {
            cursor: not-allowed;
            &,
            &:hover,
            &:focus {
                color: var(--color-text-disabled);
                background: var(--color-bg-actionable-secondary);
                border-color: var(--color-border-disabled);
            }
        }
    }
    :global(body.draggable--is-dragging) .remover {
        cursor: unset;
    }
    :global(.draggable-mirror) .remover:focus-visible:after {
        content: none;
    }
    /* hide the remover button while dragging or selected or targeted */
    :global(.draggable-mirror) .remover,
    .selected .item-btn + .remover,
    .targetable:not(.not-hoverable):hover .remover,
    .targetable:not(.not-hoverable) .item-btn:focus + .remover,
    .targetable.targeted:not(.not-hoverable) .remover {
        display: none;
    }
</style>

<!--Need to duplicate the content for screenreaders as because they do not always read it properly inside complex dom tree (i.e. math expressions)-->
<span class="hidden" id={ariaLabelElementId}><slot /></span>
<div
    class="item-btn-container"
    class:selected
    class:disabled
    class:targetable
    class:not-hoverable={notHoverable}
    class:targeted
    class:placed
    class:removable
    class:last={amount === 1}
    class:last-dragging={amount === 2}>
    <Draggable {draggableGroupKey} {disabled} {key} focusable={false} on:dragStart on:dragStop>
        {#if amount === -1 || amount > 1}
            <div class="stacking">
                {#if amount > 1}<span class="amount">{amount}</span>{/if}
                {#if amount > 2}<span class="amount-dragging">{amount - 1}</span>{/if}
            </div>
        {/if}
        <div
            role="button"
            aria-describedby={ariaDescribedBy ? ariaDescribedBy : void 0}
            aria-labelledby={ariaLabelElementId}
            aria-disabled={disabled ? true : void 0}
            class="item-btn"
            tabindex={interactive ? (tabbable ? '0' : '-1') : void 0}
            aria-grabbed={ariaGrabbed}
            on:keyup={interactive ? handleKeyUp : void 0}
            on:keydown={interactive ? handleKeyDown : void 0}
            on:click|stopPropagation={interactive ? handleClick : void 0}>
            <div class="icon-container">
                <Icon name="draggable-16" ariaHidden={true} />
            </div>
            <div class="label-container">
                <slot />
            </div>
            {#if !placed && amount > 1}
                <span class="visually-hidden" lang={instructionsLang}>
                    {__('You can use this option %d times.', amount)}
                </span>
            {/if}
            {#if !placed && amount === -1}
                <span class="visually-hidden" lang={instructionsLang}>
                    {__('You can use this option multiple times.')}
                </span>
            {/if}
        </div>
        {#if removable}
            <button
                class="remover {fixFocusMixin}"
                {disabled}
                tabindex="-1"
                aria-labelledby={`remover_${ariaLabelElementId}`}
                on:keyup={interactive ? handleRemoveKeyUp : void 0}
                on:keydown={interactive ? handleKeyDown : void 0}
                on:click|stopPropagation={interactive ? handleRemoveClick : void 0}>
                <Icon name="remove-16" ariaHidden={true} />
            </button>
            <div class="hidden" id={`remover_${ariaLabelElementId}`}>
                <slot name="remover-aria-label" />
            </div>
        {/if}
    </Draggable>
</div>
